import React, { useEffect, createElement } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useBooking } from './BookingContext';
const BOOKING_SRC = 'https://link.letlois.com/widget/booking/2ss1FbEbGXujrfA2FLKS';
const EMBED_SCRIPT = 'https://link.letlois.com/js/form_embed.js';
/**
 * A single, always-mounted booking modal. The booking iframe is rendered into
 * the DOM on first paint (kept off-screen while closed) so it preloads and
 * opens instantly the moment a RESERVE button is clicked.
 */
export function BookingModal() {
  const {
    isOpen,
    closeBooking
  } = useBooking();
  // Preload: preconnect to the booking host + inject the embed helper script once.
  useEffect(() => {
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = 'https://link.letlois.com';
    preconnect.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect);
    if (!document.querySelector(`script[src="${EMBED_SCRIPT}"]`)) {
      const script = document.createElement('script');
      script.src = EMBED_SCRIPT;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);
  // Lock body scroll + close on Escape while open.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeBooking();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, closeBooking]);
  return <>
      {/* Off-screen preloaded iframe (kept mounted so the booking widget is ready instantly) */}
      {!isOpen && <iframe src={BOOKING_SRC} title="Be CLINICAL booking — preloading" aria-hidden="true" tabIndex={-1} className="pointer-events-none fixed -left-[9999px] top-0 h-px w-px opacity-0" data-id="element-12" />}

      <AnimatePresence data-id="element-13">
        {isOpen && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.22, 1, 0.36, 1]
      }} role="dialog" aria-modal="true" aria-label="Reserve your place at the Collagen Reset Event" data-id="element-14">
            {/* Backdrop */}
            <div className="absolute inset-0 backdrop-blur-[20px] saturate-[140%]" style={{
          background: 'rgba(7, 7, 12, 0.7)'
        }} onClick={closeBooking} data-id="element-15" />

            {/* Panel */}
            <motion.div className="relative z-10 flex h-full max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-bc border border-bc-pink bg-bc-card bc-glow-box-soft" initial={{
          opacity: 0,
          y: 24,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 24,
          scale: 0.98
        }} transition={{
          duration: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-16">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-hairline px-6 py-4" data-id="element-17">
                <div data-id="element-18">
                  <p className="font-sans text-xs tracking-wide2 uppercase text-bc-pink" data-id="element-19">
                    RSVP
                  </p>
                  <p className="font-display font-extrabold text-lg text-bc-bone leading-tight" data-id="element-20">
                    RESERVE YOUR PLACE
                  </p>
                </div>
                <button onClick={closeBooking} aria-label="Close" className="rounded-full p-2 text-bc-bone-2 transition-colors bc-ease hover:text-bc-bone" data-id="element-21">
                  <X className="h-5 w-5" strokeWidth={1.5} data-id="element-22" />
                </button>
              </div>

              {/* Booking widget — the panel body scrolls so the whole form is reachable on mobile */}
              <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-bc-bone" style={{
            WebkitOverflowScrolling: 'touch'
          }} data-id="element-23">
                <iframe src={BOOKING_SRC} title="Reserve your place at the Collagen Reset Event" className="block w-full" style={{
              border: 'none',
              minHeight: '1100px'
            }} scrolling="no" data-id="element-24" />
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}